import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { ReactNode, SyntheticEvent, useMemo } from 'react';
import { useRouter } from 'next/router';

import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Link from 'presentations/views/components/atoms/Link';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import UserAvatarGroup from 'presentations/views/components/molecules/UserAvatarGroup';
import {
  dashboardGuestEventPath,
  dashboardHostEventPath,
  eventPath,
} from 'presentations/routes/path';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  white,
  primary,
  gray01,
  gray02,
  gray04,
  gray05,
  gray06,
  gray03,
} from 'presentations/views/styles/color';
import UserEventTagList from 'presentations/views/components/molecules/UserEventTagList';
import { Status } from 'domains/models/event/status';
import {
  formatDateTimeRange,
  formatLocationNameByEvent,
} from 'presentations/views/helpers/formatter';
import {
  isEventListItem,
  isLocationRemote as isLocationRemoteFunc,
  getEventUserName,
} from 'presentations/views/helpers/event';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { ViewerItem } from 'presentations/api/schema/user';
import { EventListItem } from 'presentations/api/schema/event';
import { EventCapacityItem } from 'presentations/api/schema/event_capacity';
import { SourceType } from 'domains/models/event_source/sourceType';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import { UserType } from 'domains/models/event_type_user/user_type';
import { AttendType } from 'domains/models/event_type/attendType';

interface Props {
  event: EventListItem;
  onClickMenu?: any;
  viewer: ViewerItem;
  isEditable: boolean;
  menu?: ReactNode;
}
export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();

  const event = props.event;
  const isCancel = isEventListItem(event) && event.status === Status.Cancel;
  const isPoll =
    isEventListItem(event) &&
    event.reservationMethod === ReservationMethod.Poll;
  let teamName: string | null | undefined = undefined;
  let capacity: EventCapacityItem | null | undefined = undefined;
  if (isEventListItem(event)) {
    teamName = event.isHost ? event.contact?.company : event.team?.name;
    capacity = event.capacity;
  }
  const isLocationRemote = isLocationRemoteFunc(event);

  const defaultTimeZone =
    isEventListItem(event) &&
    event.sourceType === SourceType.EventType &&
    event.isHost &&
    event.defaultTimeZone !== null
      ? event.defaultTimeZone
      : event.timeZone;

  const renderJicooEvent = (event: EventListItem) => {
    const hostUsers = event.users.filter((u) => u.userType === UserType.Host);
    const userName =
      !event.isHost && event.hostOperationType === HostOperationType.Team
        ? teamName
        : isPoll
        ? event.name
        : getEventUserName(event, true);
    return (
      <>
        <UserContainer>
          <UserName>{userName}</UserName>
          {event.attendType === AttendType.Agent ? (
            <span>{t('Agency')}</span>
          ) : (
            !isPoll && (
              <>
                {event.hostOperationType === HostOperationType.None &&
                  teamName && <CompanyName>{teamName}</CompanyName>}
                {capacity && capacity.limit > 1 && capacity.active > 1 && (
                  <OtherUsers>
                    {t('Other contacts', { num: capacity.active - 1 })}
                  </OtherUsers>
                )}
              </>
            )
          )}
        </UserContainer>
        {!isPoll && (
          <EventTitle>
            <EventCalendarColor color={event.color} />
            {event.name}
          </EventTitle>
        )}
        {capacity && (
          <EventCapacity>
            {isPoll
              ? t('Voting', {
                  active: capacity.active + capacity.cancel,
                })
              : t('Capacity', {
                  active: capacity.active,
                  limit: capacity.limit,
                })}
          </EventCapacity>
        )}
        <EventRow>
          {hostUsers.length > 0 && <UserAvatarGroup users={hostUsers} />}
          {event.locationId && (
            <Link
              href={(isLocationRemote ? event.url : eventPath(event.uid)) || ''}
            >
              <EventLocation target="_blank">
                <EventLocationIcon
                  name={isLocationRemote ? 'video' : 'chevron-right'}
                  size={fontSize(20)}
                />
                {isEventListItem(event) && formatLocationNameByEvent(event)}
              </EventLocation>
            </Link>
          )}
          {event.page && (
            <PageLink href={event.page.url} target="_blank">
              <EventLocationIcon
                name={'file-document-outline'}
                size={fontSize(20)}
              />
              {translate(`IntegrationType_${event.page.integrationId}`)}
            </PageLink>
          )}
        </EventRow>
      </>
    );
  };

  const renderContent = useMemo(
    () => (
      <>
        <EventDate>
          {formatDateTimeRange(
            event.startedAt,
            event.endedAt,
            defaultTimeZone,
            event.isAllDay,
          )}
        </EventDate>
        {event.timeZone !== defaultTimeZone && (
          <GuestEventDate>
            <GuestEventDateTitle>{t('Guest Date')}</GuestEventDateTitle>
            {formatDateTimeRange(
              event.startedAt,
              event.endedAt,
              event.timeZone,
              event.isAllDay,
            )}
            &nbsp;({event.timeZone})
          </GuestEventDate>
        )}
        {renderJicooEvent(event)}
      </>
    ),
    [event],
  );

  const onClickMenu = (e: SyntheticEvent) => {
    props.onClickMenu(e, event);
  };

  const isHost = event.attendType === AttendType.Agent || event.isHost;
  let isShowNote = true;
  if (!isHost) {
    isShowNote = false;
  }

  return (
    <Container>
      <Card isCancel={isCancel} role="button">
        <Content>
          <EventInfo isShowBorder={!isCancel && isShowNote}>
            <Header>
              <Block>
                <TagList>
                  <UserEventTagList
                    isHost={event.attendType === AttendType.Agent || isHost}
                    status={event.status}
                    flexDirection="row"
                  />
                </TagList>
                <ShownPCSite>{renderContent}</ShownPCSite>
              </Block>
            </Header>
            <ShownSPSite>{renderContent}</ShownSPSite>
          </EventInfo>
          <ActionList>
            <Link
              href={
                isHost
                  ? dashboardHostEventPath(event.uid, { from: router.asPath })
                  : dashboardGuestEventPath(event.uid, {
                      from: router.asPath,
                    })
              }
            >
              <DocumentInfoLink>
                <CalendarIcon name="calendar-text" />
                {t('Event Detail')}
                <NewWindowIcon
                  name="checkbox-multiple-blank-outline"
                  size={fontSize(13)}
                />
              </DocumentInfoLink>
            </Link>
          </ActionList>
        </Content>
        {props.isEditable && (
          <MenuContainer>
            <IconButton onClick={onClickMenu}>
              <SettingIcon name="settings" />
            </IconButton>
          </MenuContainer>
        )}
      </Card>
      {props.menu}
    </Container>
  );
}

export function LoadingComponent() {
  return (
    <Container>
      <Card>
        <Content>
          <EventInfo>
            <Header>
              <ShownPCSite>
                <SkeletonContainer>
                  <Skeleton width="300px" variant="rectangular" height="16px" />
                </SkeletonContainer>
                <SkeletonContainer>
                  <Skeleton width="200px" variant="rectangular" height="16px" />
                </SkeletonContainer>
                <SkeletonContainer>
                  <Skeleton width="360px" variant="rectangular" height="20px" />
                </SkeletonContainer>
                <SkeletonContainer>
                  <Skeleton width="100px" variant="rectangular" height="16px" />
                </SkeletonContainer>
                <SkeletonContainer>
                  <Skeleton width="60px" variant="rectangular" height="12px" />
                </SkeletonContainer>
              </ShownPCSite>
            </Header>
            <ShownSPSite>
              <UserContainer>
                <Skeleton width="200px" variant="text" />
              </UserContainer>
              <EventDate>
                <Skeleton width="200px" variant="text" />
              </EventDate>
            </ShownSPSite>
          </EventInfo>
        </Content>
      </Card>
    </Container>
  );
}

const Container = styled.div`
  margin: 0 0 ${spacing(20)};
  position: relative;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(16)};
  }
`;
const Card = styled.div<{ isCancel?: boolean }>`
  display: flex;
  background: ${white};
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  :last-of-type {
    margin-bottom: 0;
  }
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(24)};
  }

  background: ${(props) => props.isCancel && gray06};
  border-color: ${(props) => props.isCancel && gray05};
  opacity: ${(props) => props.isCancel && 0.7};

  ${mediaMax.tablet} {
    padding: ${spacing(20)};
    border: none;
  }
`;
const Content = styled.div`
  display: flex;
  width: 100%;

  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;
const EventInfo = styled.div<{
  isShowBorder?: boolean;
}>`
  width: 100%;

  border-right-width: ${(props) => props.isShowBorder && '1px'};
  border-right-color: ${(props) => props.isShowBorder && gray04};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(12)};
    border: none;
  }
`;
const ActionList = styled.div`
  width: 284px;
  margin: auto ${spacing(56)} auto ${spacing(32)};

  ${mediaMax.tablet} {
    width: 200px;
    margin: auto;
  }
`;
const DocumentInfoLink = styled.a`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 54px;
  margin-bottom: ${spacing(16)};
  border-radius: ${borderRadius(6)};
  line-height: 54px;
  text-align: center;
  color: ${primary};
  font-weight: ${fontWeight('bold')};
  background-color: ${gray06};
  border: none;

  ${mediaMax.tablet} {
    height: 36px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const NewWindowIcon = styled(Icon)`
  position: absolute;
  right: 12px;
  color: ${gray03};
`;
const Header = styled.div`
  display: flex;
  width: 100%;
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;
const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  padding-right: ${spacing(16)};
  display: flex;
  ${mediaMax.tablet} {
    margin-bottom: 0;
    flex-direction: column;
  }
`;
const UserContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: ${spacing(4)};
  }
`;
const UserName = styled.span`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-right: ${spacing(16)};

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const CompanyName = styled.span`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${gray02};
  margin-right: ${spacing(16)};

  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;
const OtherUsers = styled.span`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const EventCalendarColor = styled.span<{ color: string }>`
  display: inline-block;
  margin-right: ${spacing(8)};
  width: 8px;
  height: 8px;
  border-radius: ${borderRadius(4)};
  background-color: ${({ color }) => color};
`;
const EventTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
    font-size: ${fontSize(11)};
  }
`;
const EventCapacity = styled.div`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
    font-size: ${fontSize(11)};
  }
`;
const EventLocationIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${primary};
`;
const EventLocation = styled.a`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
  color: ${gray01};
  vertical-align: middle;
  line-height: 20px;
`;
const EventDate = styled.p`
  color: ${black};
  font-weight: ${fontWeight('bold')};
  line-height: 160%;

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
  }
`;
const GuestEventDate = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing(4)} 0;
  color: ${gray01};
  font-size: ${fontSize(12)};
  word-break: break-all;

  ${mediaMax.tablet} {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const GuestEventDateTitle = styled.div`
  flex-shrink: 0;
  background-color: ${gray05};
  border-radius: ${borderRadius(4)};
  font-size: ${fontSize(10)};
  padding: ${spacing(4)};
  margin-right: ${spacing(4)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(4)};
    margin-right: 0;
  }
`;
const MenuContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;
const ShownPCSite = styled.div`
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ShownSPSite = styled.div`
  display: none;
  ${mediaMax.tablet} {
    display: block;
  }
`;
const Block = styled.div`
  ${mediaMax.tablet} {
    margin-right: ${spacing(24)};
  }
`;
const CalendarIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  font-size: ${fontSize(20)};
`;

const SkeletonContainer = styled.div`
  margin-bottom: ${spacing(16)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;

const SettingIcon = styled(Icon)`
  color: ${gray01};
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
`;

const PageLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${black};
  margin-left: ${spacing(12)};
`;
